import React, { useCallback, useEffect, useRef } from "react";
import { Button, Space, Tooltip, message } from "antd";
import {
  BoldOutlined,
  ClearOutlined,
  ItalicOutlined,
  LinkOutlined,
  OrderedListOutlined,
  PictureOutlined,
  UnderlineOutlined,
  UnorderedListOutlined,
} from "@ant-design/icons";
import { EditorContent, useEditor } from "@tiptap/react";
import { buildExtensions, normalizeEditorHtml } from "./richTextHtml";
import { imageFilesFrom, pickImageFile, uploadInlineImage } from "./imageUpload";
import "./RichTextEditor.scss";

/**
 * The rich-text field, used for post bodies and for `richtext` page fields.
 *
 * Controlled, and empty means empty string: both call sites treat `""` as "no
 * value" — the post has no body, the page field falls back to the site's
 * committed copy — so the editor must never hand back the empty paragraph its
 * document actually is. `normalizeEditorHtml` owns that, along with the rest of
 * the stored-HTML contract; see `richTextHtml.js`.
 */

// Gloria edits from a phone: every control clears the 44px touch target.
const TOOL = { minWidth: 44, height: 44 };

const Tool = ({ title, isActive, onClick, icon, children }) => (
  <Tooltip title={title}>
    <Button
      type={isActive ? "primary" : "default"}
      icon={icon}
      style={TOOL}
      aria-label={title}
      aria-pressed={Boolean(isActive)}
      // The selection is what the button acts on, so the button must not take
      // focus away from it first.
      onMouseDown={(event) => event.preventDefault()}
      onClick={onClick}
    >
      {children}
    </Button>
  </Tooltip>
);

export default function RichTextEditor({ value, onChange, placeholder }) {
  // `onUpdate` is installed once with the editor; a ref keeps it calling the
  // current handler without recreating the editor and losing the caret.
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const editorRef = useRef(null);

  /** Upload and insert at `position`, or at the caret when it is undefined. */
  const insertImages = useCallback(async (files, position) => {
    const editor = editorRef.current;
    if (!editor) return;
    for (const file of files) {
      try {
        const attributes = await uploadInlineImage(file);
        const chain = editor.chain().focus();
        if (typeof position === "number")
          chain.insertContentAt(position, { type: "image", attrs: attributes });
        else chain.setImage(attributes);
        chain.run();
      } catch (error) {
        message.error(`Качването не успя: ${error.message}`);
      }
    }
  }, []);

  const editor = useEditor({
    extensions: buildExtensions(),
    content: value || "",
    editorProps: {
      attributes: { class: "rich-text-editor__content" },
      // A dropped or pasted image file is uploaded and linked, the same way the
      // toolbar button does it. Base64 pasted as *markup* is a different path,
      // caught before save by `uploadDataImages`.
      handleDrop: (view, event, slice, moved) => {
        if (moved) return false;
        const files = imageFilesFrom(event.dataTransfer);
        if (files.length === 0) return false;
        event.preventDefault();
        const at = view.posAtCoords({
          left: event.clientX,
          top: event.clientY,
        });
        insertImages(files, at?.pos);
        return true;
      },
      handlePaste: (view, event) => {
        const files = imageFilesFrom(event.clipboardData);
        if (files.length === 0) return false;
        event.preventDefault();
        insertImages(files);
        return true;
      },
    },
    onUpdate: ({ editor: instance }) =>
      onChangeRef.current(normalizeEditorHtml(instance.getHTML())),
  });

  editorRef.current = editor;

  // A value that changed elsewhere — a post loading, a page field seeded or
  // reverted — has to reach the document; a value that is merely the echo of
  // what was just typed must not, or every keystroke would reset the caret.
  useEffect(() => {
    if (!editor) return;
    const next = value || "";
    if (next === normalizeEditorHtml(editor.getHTML())) return;
    editor.commands.setContent(next, { emitUpdate: false });
  }, [editor, value]);

  const chooseImage = async () => {
    const file = await pickImageFile();
    if (file) await insertImages([file]);
  };

  const editLink = () => {
    const previous = editor.getAttributes("link").href || "";
    const href = window.prompt("Адрес на връзката:", previous);
    if (href === null) return;
    if (href === "") {
      editor.chain().focus().unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange("link").setLink({ href }).run();
  };

  if (!editor) return null;

  return (
    <div className="rich-text-editor">
      <Space wrap size={4} className="rich-text-editor__toolbar">
        <Tool
          title="Заглавие 2"
          isActive={editor.isActive("heading", { level: 2 })}
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
        >
          H2
        </Tool>
        <Tool
          title="Заглавие 3"
          isActive={editor.isActive("heading", { level: 3 })}
          onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
        >
          H3
        </Tool>
        <Tool
          title="Удебелен"
          icon={<BoldOutlined />}
          isActive={editor.isActive("bold")}
          onClick={() => editor.chain().focus().toggleBold().run()}
        />
        <Tool
          title="Курсив"
          icon={<ItalicOutlined />}
          isActive={editor.isActive("italic")}
          onClick={() => editor.chain().focus().toggleItalic().run()}
        />
        <Tool
          title="Подчертан"
          icon={<UnderlineOutlined />}
          isActive={editor.isActive("underline")}
          onClick={() => editor.chain().focus().toggleUnderline().run()}
        />
        <Tool
          title="Списък с точки"
          icon={<UnorderedListOutlined />}
          isActive={editor.isActive("bulletList")}
          onClick={() => editor.chain().focus().toggleBulletList().run()}
        />
        <Tool
          title="Номериран списък"
          icon={<OrderedListOutlined />}
          isActive={editor.isActive("orderedList")}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
        />
        <Tool
          title="Връзка"
          icon={<LinkOutlined />}
          isActive={editor.isActive("link")}
          onClick={editLink}
        />
        <Tool
          title="Цитат"
          isActive={editor.isActive("blockquote")}
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
        >
          ❝
        </Tool>
        <Tool title="Изображение" icon={<PictureOutlined />} onClick={chooseImage} />
        <Tool
          title="Изчисти форматирането"
          icon={<ClearOutlined />}
          onClick={() => editor.chain().focus().unsetAllMarks().clearNodes().run()}
        />
      </Space>
      <div className="rich-text-editor__body">
        {placeholder && !value && (
          <div className="rich-text-editor__placeholder">{placeholder}</div>
        )}
        <EditorContent editor={editor} />
      </div>
    </div>
  );
}
